import { Banner, BlockStack, Button, Spinner, Text } from "@shopify/polaris";
import {
  QueryClient,
  QueryClientProvider,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import {
  type ReactNode,
  createContext,
  useContext,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import { useLocation } from "react-router-dom";

import {
  bindActiveTenantShopGeneration,
  canonicalShopDomain,
  currentClientShopDomain,
} from "./api/client";
import { getMe } from "./api/me";
import { isStockyPublicBigIntId } from "./api/stocky_public_ids";
import {
  type AuthoritativeShopState,
  useAuthoritativeShop,
} from "./hooks/useAuthoritativeShop";
import { t } from "./lib/i18n";
import type { MeResponse } from "./types";

export interface VerifiedShopGeneration {
  shopId: string;
  shopDomain: string;
  shopGeneration: string;
}

const VerifiedShopGenerationContext =
  createContext<VerifiedShopGeneration | null>(null);

export interface VerifiedEntitlementGeneration {
  /** Changes whenever the authoritative active subscription changes. */
  entitlementGeneration: string;
}

const VerifiedEntitlementGenerationContext =
  createContext<VerifiedEntitlementGeneration | null>(null);

/** Tenant-only identity. Calling outside the verified boundary fails closed. */
export function useVerifiedShopGeneration(): VerifiedShopGeneration {
  const value = useContext(VerifiedShopGenerationContext);
  if (value === null) {
    throw new Error("verified shop generation is unavailable");
  }
  return value;
}

/** Current server-authoritative plan generation. Missing context fails closed. */
export function useVerifiedEntitlementGeneration(): VerifiedEntitlementGeneration {
  const value = useContext(VerifiedEntitlementGenerationContext);
  if (value === null) {
    throw new Error("verified entitlement generation is unavailable");
  }
  return value;
}

export function stockyEntitlementGeneration(data: MeResponse): string {
  const active = data.subscription.active === true;
  const planCode =
    active &&
    typeof data.subscription.plan_code === "string" &&
    data.subscription.plan_code.length > 0
      ? data.subscription.plan_code
      : "free";
  return `${active ? "active" : "inactive"}:${planCode}`;
}

/**
 * Explicit provider for isolated screenshot/test harnesses that already own a
 * QueryClient. It never infers identity and applies the same owner binding and
 * next-render mount rule as the production boundary.
 */
export function VerifiedShopGenerationProvider({
  value,
  entitlementGeneration = "explicit",
  children,
}: {
  value: VerifiedShopGeneration;
  entitlementGeneration?: string;
  children: ReactNode;
}) {
  const canonical = canonicalShopDomain(value.shopDomain);
  const valid =
    typeof value.shopId === "string" &&
    isStockyPublicBigIntId(value.shopId) &&
    canonical !== null &&
    value.shopGeneration === `${value.shopId}:${canonical}`;
  const [boundGeneration, setBoundGeneration] = useState<string | null>(null);

  useLayoutEffect(() => {
    if (!valid || canonical === null) return;
    const unbind = bindActiveTenantShopGeneration(canonical);
    setBoundGeneration(value.shopGeneration);
    return () => {
      unbind();
    };
  }, [canonical, valid, value.shopGeneration]);

  if (!valid || canonical === null) {
    throw new Error("explicit verified shop generation is invalid");
  }
  if (boundGeneration !== value.shopGeneration) return null;

  return (
    <VerifiedShopGenerationContext.Provider
      value={{ ...value, shopDomain: canonical }}
    >
      <VerifiedEntitlementGenerationContext.Provider
        value={{ entitlementGeneration }}
      >
        {children}
      </VerifiedEntitlementGenerationContext.Provider>
    </VerifiedShopGenerationContext.Provider>
  );
}

export function createAppQueryClient(): QueryClient {
  return new QueryClient({
    defaultOptions: {
      queries: {
        retry: 3,
        retryDelay: (attempt) => Math.min(1_000 * 2 ** attempt, 8_000),
        refetchOnWindowFocus: false,
        staleTime: 30_000,
      },
    },
  });
}

function useDisposeQueryClient(client: QueryClient): void {
  const activeEffects = useRef(0);
  useEffect(() => {
    activeEffects.current += 1;
    return () => {
      activeEffects.current -= 1;
      // StrictMode immediately replays effects without unmounting state. Defer
      // one microtask so that replay can reclaim the same client; a real shop
      // transition leaves the counter at zero and destroys it deterministically.
      queueMicrotask(() => {
        if (activeEffects.current !== 0) return;
        // cancelQueries signals every active query before the cache becomes
        // unreachable. clear also discards mutations; central generation guards
        // prevent a non-abortable old request from using the next shop's token.
        void client.cancelQueries();
        client.clear();
      });
    };
  }, [client]);
}

function PendingShop({ conflicted = false }: { conflicted?: boolean }) {
  return (
    <BlockStack gap="300" inlineAlign="center">
      {conflicted ? (
        <Banner tone="critical">
          {t("ストアを安全に確認できませんでした。Shopify管理画面からアプリを開き直してください。")}
        </Banner>
      ) : (
        <>
          <Spinner accessibilityLabel={t("ストアを確認中")} size="small" />
          <Text as="p">{t("ストアを確認中です…")}</Text>
        </>
      )}
    </BlockStack>
  );
}

const PLAN_SENSITIVE_QUERY_ROOTS = new Set([
  "stocky-data",
  "stocky-migration",
]);

function isPlanSensitiveKey(key: readonly unknown[]): boolean {
  return (
    typeof key[0] === "string" && PLAN_SENSITIVE_QUERY_ROOTS.has(key[0])
  );
}

/**
 * A plan transition is another data-visibility generation inside one tenant.
 * Hide descendants first, then cancel and remove every plan-sensitive query
 * and mutation before accepting the new generation. A late paid response is
 * consequently detached from both the active observer and the cache.
 */
function EntitlementGenerationBoundary({
  initialMe,
  children,
}: {
  initialMe: MeResponse;
  children: ReactNode;
}) {
  const queryClient = useQueryClient();
  const meQuery = useQuery({
    queryKey: ["me"],
    queryFn: getMe,
    staleTime: 0,
    refetchOnMount: "always",
    refetchOnWindowFocus: "always",
    // Shopify billing changes can arrive out of band. Poll while the embedded
    // app is open so a same-tab cancellation has a bounded convergence time;
    // sensitive Stocky Data descendants additionally hide during refresh.
    refetchInterval: 30_000,
  });
  const me = meQuery.data;
  const canonicalDomain = canonicalShopDomain(me?.shopify_domain);
  const identityValid =
    me !== undefined &&
    me.shop_id === initialMe.shop_id &&
    canonicalDomain === canonicalShopDomain(initialMe.shopify_domain);
  const nextGeneration = identityValid
    ? stockyEntitlementGeneration(me)
    : null;
  const [acceptedGeneration, setAcceptedGeneration] = useState(() =>
    stockyEntitlementGeneration(initialMe),
  );

  useLayoutEffect(() => {
    if (
      nextGeneration === null ||
      nextGeneration === acceptedGeneration
    ) {
      return;
    }
    let active = true;
    void (async () => {
      await queryClient.cancelQueries({
        predicate: (query) => isPlanSensitiveKey(query.queryKey),
      });
      queryClient.removeQueries({
        predicate: (query) => isPlanSensitiveKey(query.queryKey),
      });
      for (const mutation of queryClient.getMutationCache().getAll()) {
        if (isPlanSensitiveKey(mutation.options.mutationKey ?? [])) {
          queryClient.getMutationCache().remove(mutation);
        }
      }
      if (active) setAcceptedGeneration(nextGeneration);
    })();
    return () => {
      active = false;
    };
  }, [acceptedGeneration, nextGeneration, queryClient]);

  if (nextGeneration === null) {
    return <PendingShop conflicted />;
  }
  if (nextGeneration !== acceptedGeneration) {
    return <PendingShop />;
  }

  return (
    <VerifiedEntitlementGenerationContext.Provider
      value={{ entitlementGeneration: acceptedGeneration }}
    >
      {children}
    </VerifiedEntitlementGenerationContext.Provider>
  );
}

function TenantQueryBoundary({
  shop,
  children,
}: {
  shop: AuthoritativeShopState;
  children: ReactNode;
}) {
  if (
    shop.data === undefined ||
    shop.shopId === null ||
    shop.shopDomain === null ||
    shop.shopGeneration === null
  ) {
    throw new Error("tenant query boundary requires a verified shop");
  }
  const verifiedShop: VerifiedShopGeneration = {
    shopId: shop.shopId,
    shopDomain: shop.shopDomain,
    shopGeneration: shop.shopGeneration,
  };
  const [client] = useState(() => {
    const next = createAppQueryClient();
    // Legacy consumers still use ["me"]. Only the freshly verified identity
    // is copied into this brand-new, shop-owned cache.
    next.setQueryData(["me"], shop.data);
    next.setQueryData(
      ["me", "shop-generation", shop.shopDomain],
      shop.data,
    );
    return next;
  });
  useDisposeQueryClient(client);

  return (
    <VerifiedShopGenerationProvider value={verifiedShop}>
      <QueryClientProvider client={client}>
        <EntitlementGenerationBoundary initialMe={shop.data}>
          {children}
        </EntitlementGenerationBoundary>
      </QueryClientProvider>
    </VerifiedShopGenerationProvider>
  );
}

function AuthoritativeShopGate({ children }: { children: ReactNode }) {
  const shop = useAuthoritativeShop();

  if (shop.isError) {
    return (
      <BlockStack gap="300" inlineAlign="center">
        <Banner tone="critical">
          {t("ストア情報を確認できませんでした。時間をおいて再試行してください。")}
        </Banner>
        <Button onClick={shop.retry}>{t("再試行")}</Button>
      </BlockStack>
    );
  }
  if (
    shop.isLoading ||
    shop.data === undefined ||
    shop.shopGeneration === null ||
    shop.shopDomain === null
  ) {
    return <PendingShop />;
  }

  return (
    <TenantQueryBoundary key={shop.shopGeneration} shop={shop}>
      {children}
    </TenantQueryBoundary>
  );
}

function BootstrapQueryBoundary({ children }: { children: ReactNode }) {
  const [client] = useState(createAppQueryClient);
  useDisposeQueryClient(client);

  return (
    <QueryClientProvider client={client}>
      <AuthoritativeShopGate>{children}</AuthoritativeShopGate>
    </QueryClientProvider>
  );
}

/**
 * Process-wide tenant boundary. A same-document A -> B -> A navigation gets a
 * fresh bootstrap client and a fresh tenant client on every visit. Conflicting
 * URL/App Bridge/dev-token signals unmount tenant UI before any request starts.
 */
export function ShopGenerationBoundary({ children }: { children: ReactNode }) {
  const location = useLocation();
  const requestedShopDomain = currentClientShopDomain(location.search);
  const visit = useRef<{ shop: string | null; sequence: number }>({
    shop: requestedShopDomain,
    sequence: 0,
  });
  if (visit.current.shop !== requestedShopDomain) {
    visit.current = {
      shop: requestedShopDomain,
      sequence: visit.current.sequence + 1,
    };
  }

  if (requestedShopDomain === null) {
    const hasShopSignal =
      new URLSearchParams(location.search).has("shop") ||
      Boolean(window.shopify?.config?.shop);
    return <PendingShop conflicted={hasShopSignal} />;
  }

  return (
    <BootstrapQueryBoundary
      key={`${requestedShopDomain}:${visit.current.sequence}`}
    >
      {children}
    </BootstrapQueryBoundary>
  );
}
